import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { ReserveChangeReview } from '@client/components/claims/financials/ReserveChangeReview';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { ENABLE_CLAIMS_AUTHORITY_FLAG } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';

/**
 * CUR-1642: a held reserve change on the claim's Financials tab. The person it is with reviews
 * and decides it here; anyone else who can read the claim sees it without the decision buttons.
 */
export const Route = createFileRoute('/console/$orgId/claims/details/$id/financials/reserve-changes/$requestId')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Reserve change',
        },
        // Hidden with the rest of claims authority; the server answers NOT_FOUND while it is off.
        featureFlag: ENABLE_CLAIMS_AUTHORITY_FLAG,
    },
    params: {
        parse({ requestId }) {
            return {
                requestId: BigInt(requestId),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ReserveChangePanel />
        </PageController>
    );
}

function ReserveChangePanel() {
    const { requestId } = Route.useParams();
    const navigate = useNavigate();
    const { data, error, refetch, isFetching } = trpc.claimAuthority.getReserveChange.useQuery(
        { requestId },
        { meta: { errorSurface: 'inline' } },
    );

    return (
        <OverlayPage
            title="Reserve change"
            size="small"
            isDirty={() => false}
            afterClose={() => navigate({ moveInMatches: -1 })}
            footer={({ onClose }) => <DrawerFooter onCancel={onClose} cancelText="Close" showOk={false} />}
        >
            {({ onClose }) =>
                data ? (
                    <ReserveChangeReview review={data} onDecided={onClose} />
                ) : error ? (
                    <InlineQueryError
                        error={error}
                        onRetry={() => void refetch()}
                        retrying={isFetching}
                        description="Could not load this reserve change."
                    />
                ) : (
                    <Loading />
                )
            }
        </OverlayPage>
    );
}
